/**
 * The auth constants both sides need.
 *
 * ARCH §7.4: forms share "the *same* Zod schemas the API validates against — so
 * a client-side message and a server-side rejection cannot disagree." The same
 * argument applies to the numbers underneath them. Before this module, ten
 * characters was written in three places and thirty seconds in two, and a
 * screen promising one wait while the server enforced another is a bug nobody
 * would think to test for.
 *
 * `packages/schema` imports no Cloudflare binding, which is what lets the API
 * Worker and the React dashboard read one definition (ARCH §2.3, DECISION-004).
 *
 * Story 3.4.
 */

/**
 * PRD §5.1 / ARCH §5.1: "Password minimum 10 characters, no composition rules
 * — three unrelated words beats one clever substitution."
 */
export const PASSWORD_MIN_LENGTH = 10

/** Better Auth's own ceiling. Named so a 129-character phrase fails loudly. */
export const PASSWORD_MAX_LENGTH = 128

/**
 * Link lifetimes, in seconds, as the screens state them:
 *   · 24 hours  — `auth-verify.html`, "Confirmation links last 24 hours."
 *   · one hour  — `auth-reset.html`,  "The link works for one hour."
 *   · seven days — `auth-invite.html`, "Invitations last seven days."
 */
export const LINK_LIFETIME_SECONDS = {
  verify: 60 * 60 * 24,
  reset: 60 * 60,
  invitation: 60 * 60 * 24 * 7,
} as const

/**
 * `auth-verify.html` and `auth-reset.html` both render "Resend in 30s".
 *
 * The mockups' note says why it is a countdown rather than a limit: "Resend is
 * disabled on a visible countdown rather than silently rate-limited." A person
 * who cannot see the wait clicks again, and a button that does nothing reads as
 * broken.
 */
export const RESEND_COOLDOWN_SECONDS = 30

/** "5 minutes", "1 minute", "a moment" — one phrasing, both sides. */
export function waitPhrase(seconds: number): string {
  if (seconds <= 0) return 'a moment'
  const minutes = Math.ceil(seconds / 60)
  return minutes === 1 ? '1 minute' : `${minutes} minutes`
}

/** The password advice the meter gives. Advice, never a score (ARCH §5.1). */
export function passwordAdvice(password: string): { filled: number; advice: string } {
  const words = password.trim().split(/\s+/).filter(Boolean).length
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      filled: 0,
      advice: `Use ${PASSWORD_MIN_LENGTH} characters or more. A short phrase is stronger than a scramble.`,
    }
  }
  if (words >= 3) return { filled: 3, advice: 'Strong — a phrase like that takes years to guess' }
  if (words === 2) {
    return { filled: 2, advice: 'Good. Add another word and it gets much harder to guess.' }
  }
  return {
    filled: 1,
    advice: 'Add another word — three unrelated words beats one clever substitution.',
  }
}

/**
 * The meter as a CHECKLIST — `settings-profile.html`'s "✓ Long enough" beside
 * "◐ Add another word".
 *
 * `passwordAdvice` above returns ONE sentence, which is what the auth screens
 * draw: signing up, the person is doing one thing and the next instruction is
 * all they need. `settings-profile.html` draws both criteria at once, met and
 * unmet together, because somebody changing a password already has one and is
 * deciding whether the new one is better.
 *
 * Two renderings, one rule — the same two thresholds decide both, so the
 * sentence and the ticks cannot disagree. ARCH §7.4 applied to a number, the
 * way `LINK_LIFETIME_SECONDS` is.
 *
 * `met` is the whole of it. The GLYPH is the screen's (✓ and ◐) and so is the
 * colour; this module holds no presentation, which is what lets the embed
 * runtime import `@leadgible/schema` without importing a design system.
 *
 * Story 4.3.
 */
export type PasswordCheck = { readonly id: 'length' | 'words'; readonly label: string; readonly met: boolean }

export function passwordChecklist(password: string): PasswordCheck[] {
  const words = password.trim().split(/\s+/).filter(Boolean).length
  const longEnough = password.length >= PASSWORD_MIN_LENGTH
  // Three unrelated words is the advice everywhere else in the product; two is
  // where the meter stops nagging, which is what "Add another word" means when
  // you already have two.
  const enoughWords = words >= 3

  return [
    {
      id: 'length',
      label: longEnough ? 'Long enough' : `Use ${PASSWORD_MIN_LENGTH} characters or more`,
      met: longEnough,
    },
    {
      id: 'words',
      label: enoughWords ? 'Three unrelated words' : 'Add another word',
      met: enoughWords,
    },
  ]
}
